import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Grid } from '@mui/material';
import { EventProfile } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faIdCardClip } from '@fortawesome/pro-solid-svg-icons/faIdCardClip';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../shared/layout/DetailForm';
import { GoToProgramButton } from '../shared/topbar/GoToProgramButton';
import { GridListDetails } from '../profiles/components/detail/GridListDetails';
import { ProfileDetailsTitle } from '../profiles/components/detail';
import { ProfileDisplayName } from '../profiles/utils/ProfileDisplayName';
import { SxStyles } from '../shared/utils/types';
import { isInShell } from '../shell/shell';
import { profilesPageSize, useTestUsers } from './TestUsersContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';
import { usePrograms } from '../loyalty/ProgramsContext';

const styles: SxStyles = {
  card: {
    backgroundColor: colorPalette.grey.light,
    borderTop: ({ blocked }) =>
      `4px solid ${blocked ? colorPalette.red.main : colorPalette.blue.main}`,
    marginBottom: 2,
  },
  cardHeader: {
    height: 66,
    borderBottom: `1px solid ${colorPalette.grey.main}`,
    padding: [2, 3],
  },
  cardTitle: {
    display: 'inline',
    margin: [0, 1],
    color: colorPalette.marine.main,
    '&.svg': {
      color: colorPalette.marine.main,
    },
  },
  cardContent: {
    padding: [2, 3],
  },
};

export function ExistingTestUserDetails() {
  const {
    testUsers,
    selectedTestUser,
    currentPage,
    setSelectedTestUser,
    goToPreviousPage,
    goToNextPage,
  } = useTestUsers();
  const { t } = useTranslation();
  const { programId } = useProgram();
  const { profileApi } = useApi();
  const [identities, setIdentities] = useState<EventProfile[]>([]);

  if (!selectedTestUser) {
    throw new Error('no test user selected');
  }
  const currentIndex = testUsers?.data.indexOf(selectedTestUser);

  const cannotGoToNextPage = currentIndex >= testUsers?.data.length - 1 && testUsers.isLast;
  const cannotGoToPreviousPage = currentIndex <= 0 && currentPage === 0;

  const handleNext = () => {
    if (currentIndex + 1 === profilesPageSize) {
      goToNextPage();
    } else {
      const nextProfile = testUsers.data[currentIndex + 1];
      setSelectedTestUser(nextProfile);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0 && currentIndex === 0) {
      goToPreviousPage();
    } else {
      const previousProfile = testUsers.data[currentIndex - 1];
      setSelectedTestUser(previousProfile);
    }
  };

  const fetchIdentities = async (profileId: string) => {
    profileApi
      .getProfileIdentities(profileId)
      .then(datas =>
        setIdentities(
          datas.filter(data => data.programId === null || data.programId === programId),
        ),
      )
      .catch(() => setIdentities([]));
  };

  useEffect(() => {
    fetchIdentities(selectedTestUser.id).catch(console.error);
  }, [selectedTestUser]);

  const identityItems = identities.map(identity => {
    return { left: identity.kind, right: <div> {identity.value} </div>, key: identity.kind };
  });

  const { programs } = usePrograms();

  const programItems =
    programs?.map(program => {
      const membership = selectedTestUser?.memberships?.find(m => m.programId === program.id);
      return {
        left: program.name,
        right: (
          <Box
            sx={{
              justifyContent: 'flex-end',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {membership && (
              <a
                style={{ color: '#61c1db', textDecoration: 'none !important' }}
                href={`${isInShell() ? '/s/loyalty' : '/loy'}/accounts/${
                  selectedTestUser.accountId
                }/programs/${membership.programId}/profiles?search=${selectedTestUser.id}`}
                target="_blank"
                rel="noreferrer"
              >
                {t('test-users.details.programs.crm')}
              </a>
            )}
            <GoToProgramButton
              programId={program.id}
              profileId={selectedTestUser.id}
              variant="link"
              sx={{ marginLeft: '10px' }}
            />
          </Box>
        ),
        key: membership?.id || program.id,
      };
    }) || [];

  return (
    <>
      <DetailForm>
        <DetailForm.Form>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <ProfileDetailsTitle>
              <Box sx={{ alignItems: 'center', display: 'flex' }}>
                <Box>
                  <ProfileDisplayName profile={selectedTestUser} />
                </Box>
              </Box>
            </ProfileDetailsTitle>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ display: 'flex', marginBottom: 1 }}>
                <Button color="grey" onClick={handlePrevious} disabled={cannotGoToPreviousPage}>
                  <FontAwesomeIcon fontSize="inherit" icon={faChevronLeft} />
                </Button>
                <Button
                  color="grey"
                  onClick={handleNext}
                  disabled={cannotGoToNextPage}
                  sx={{ marginLeft: 0.5 }}
                >
                  <FontAwesomeIcon fontSize="inherit" icon={faChevronRight} />
                </Button>
              </Box>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card sx={styles.card}>
                <CardHeader
                  sx={styles.cardHeader}
                  titleTypographyProps={{
                    variant: 'h3',
                  }}
                  title={
                    <div>
                      <FontAwesomeIcon icon={faIdCardClip} color={colorPalette.marine.main} />
                      <Box sx={styles.cardTitle}>{t('profiles.details.identity.title')}</Box>
                    </div>
                  }
                />
                <CardContent sx={styles.cardContent}>
                  <GridListDetails items={identityItems} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={styles.card}>
                <CardHeader
                  sx={styles.cardHeader}
                  titleTypographyProps={{
                    variant: 'h3',
                  }}
                  title={
                    <div>
                      <FontAwesomeIcon icon={faUsers} color={colorPalette.marine.main} />
                      <Box sx={styles.cardTitle}>{t('test-users.details.programs.title')}</Box>
                    </div>
                  }
                />
                <CardContent sx={styles.cardContent}>
                  <GridListDetails
                    items={programItems}
                    messageNoItems={t('test-users.details.programs.no-programs')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DetailForm.Form>
      </DetailForm>
    </>
  );
}
